/**
 * Messages page — standard and end-to-end encrypted messaging for KaiMed patients.
 *
 * Tabs:
 *  - "All Messages"    — existing message list / thread / compose flow.
 *  - "Secure Messages" — E2E encrypted chat via SecureChat component (Phase 3C).
 */

import React, { useState, useCallback } from 'react';
import {
  Search,
  Filter,
  Plus,
  X,
  Send,
  Lock,
  MessageSquare,
} from 'lucide-react';
import useMessages from '../hooks/use-messages';
import MessageList from '../components/messages/conversation-list';
import MessageThread from '../components/messages/message-thread';
import ComposeMessage from '../components/messages/compose-message';
import HealthDataSharing from '../components/messages/health-data-sharing';
import SecureChat, { type SecureChatProvider } from '../components/messages/SecureChat';
import { useAuth } from '../hooks/use-auth';
import { supabase } from '../lib/supabase-client';

// ---------------------------------------------------------------------------
// Mock secure contacts — in production this list comes from the patient's
// care team API, including each provider's current ECDH public key JWK.
// ---------------------------------------------------------------------------

const SECURE_CONTACTS: SecureChatProvider[] = [
  {
    id: 'provider-001',
    name: 'Dr. Sarah Johnson',
    role: 'Primary Care',
    // publicKeyJwk would be fetched from the server and populated here
  },
  {
    id: 'provider-002',
    name: 'Dr. Michael Chen',
    role: 'Cardiology',
  },
  {
    id: 'provider-003',
    name: 'Dr. Emily Rodriguez',
    role: 'Dermatology',
  },
];

// ---------------------------------------------------------------------------
// Tab type
// ---------------------------------------------------------------------------

type MessagesTab = 'all' | 'secure';

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const Messages: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<MessagesTab>('all');

  // Standard messages state
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isDataSharingOpen, setIsDataSharingOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  // Secure messages state — which provider is open in SecureChat
  const [selectedSecureProviderId, setSelectedSecureProviderId] = useState<string | null>(null);

  const { user } = useAuth();

  const {
    messages,
    isLoading,
    filters,
    updateFilters,
  } = useMessages();

  const selectedMessage = messages.find(msg => msg.id === selectedMessageId);
  const selectedSecureProvider = SECURE_CONTACTS.find(p => p.id === selectedSecureProviderId);

  // ---------------------------------------------------------------------------
  // Standard messages handlers
  // ---------------------------------------------------------------------------

  const handleOpenCompose = () => {
    setIsComposeOpen(true);
    setSelectedMessageId(null);
  };

  const handleCloseCompose = () => {
    setIsComposeOpen(false);
  };

  const handleSelectMessage = (messageId: string) => {
    setSelectedMessageId(messageId);
    setIsComposeOpen(false);
  };

  const handleBackFromThread = () => {
    setSelectedMessageId(null);
  };

  const handleOpenDataSharing = () => {
    setIsDataSharingOpen(true);
  };

  const handleCloseDataSharing = () => {
    setIsDataSharingOpen(false);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    updateFilters({ searchTerm: e.target.value });
  };

  // Insert a message row into the chat_messages table.
  // session_id is set to the currently selected message thread id (or a new
  // session derived from the recipient when composing a fresh message).
  const handleSendMessage = useCallback(
    async (content: string, recipientId: string, _subject?: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const sessionId = selectedMessageId ?? `new-${session.user.id}-${recipientId}-${Date.now()}`;

      const { error } = await supabase.from('chat_messages').insert({
        session_id: sessionId,
        role: 'user',
        content,
        metadata: { sender_name: user?.firstName || 'Patient' },
      });

      if (error) {
        console.error('Failed to send message:', error.message);
      }
    },
    [selectedMessageId, user?.firstName],
  );

  // ---------------------------------------------------------------------------
  // Secure messages handlers
  // ---------------------------------------------------------------------------

  const handleSelectSecureProvider = (providerId: string) => {
    setSelectedSecureProviderId(providerId);
  };

  const handleBackFromSecureChat = () => {
    setSelectedSecureProviderId(null);
  };

  /**
   * Called by SecureChat whenever the local ECDH public key is generated or
   * rotated. In production, persist this to the server so the provider can
   * derive the shared secret from their side.
   */
  const handlePublicKeyReady = useCallback((jwk: JsonWebKey) => {
    console.info(
      '[SecureMessaging] Local public key ready — persist to server:',
      JSON.stringify(jwk),
    );
    // TODO: POST jwk to /api/v1/secure-messages/public-key
  }, []);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const tabClass = (tab: MessagesTab) =>
    `flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
      activeTab === tab
        ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]'
        : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-subtle)]'
    }`;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="h-[calc(100vh-11rem)] flex flex-col">

      {/* Page header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Messages</h1>
        {activeTab === 'all' && (
          <button
            onClick={handleOpenCompose}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg hover:bg-[var(--brand-primary-hover)] transition-colors"
          >
            <Plus size={16} />
            <span>New Message</span>
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-[var(--border-subtle)] mb-0 flex-shrink-0">
        <button
          className={tabClass('all')}
          onClick={() => setActiveTab('all')}
        >
          <MessageSquare size={15} />
          All Messages
        </button>
        <button
          className={tabClass('secure')}
          onClick={() => setActiveTab('secure')}
        >
          <Lock size={15} />
          Secure Messages
          {/* Badge to distinguish encrypted conversations */}
          <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
            E2E
          </span>
        </button>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* TAB: All Messages                                                   */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'all' && (
        <div className="grid md:grid-cols-[350px_1fr] flex-1 rounded-3xl border border-[var(--border-subtle)] shadow-sm bg-white/70 dark:bg-slate-800/60 backdrop-blur-md overflow-hidden mt-4">
          {/* Sidebar */}
          <div
            className={`flex flex-col bg-transparent ${
              selectedMessageId ? 'hidden md:flex' : 'flex'
            } overflow-hidden`}
          >
            {/* Search and filters */}
            <div className="flex-shrink-0 p-4 border-b border-[var(--border-subtle)]">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--background-surface)] text-[var(--text-primary)]"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-[var(--text-secondary)]" />
              </div>
              <div className="flex items-center justify-between mt-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <Filter size={16} />
                  <span>Filters</span>
                </button>
                {filters.unreadOnly && (
                  <button
                    onClick={() => updateFilters({ unreadOnly: false })}
                    className="flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    <span>Clear filters</span>
                    <X size={14} />
                  </button>
                )}
              </div>
              {showFilters && (
                <div className="mt-3 p-3 bg-[var(--background-surface)] rounded-lg border border-[var(--border-subtle)]">
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id="unread-only"
                      checked={filters.unreadOnly}
                      onChange={(e) => updateFilters({ unreadOnly: e.target.checked })}
                      className="mr-2"
                    />
                    <label htmlFor="unread-only" className="text-sm text-[var(--text-primary)]">
                      Unread only
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Conversation list */}
            <div className="flex-1 overflow-y-auto">
              <MessageList
                messages={messages}
                selectedId={selectedMessageId}
                onSelectMessage={handleSelectMessage}
                isLoading={isLoading}
              />
            </div>
          </div>

          {/* Thread / compose panel */}
          <div
            className={`flex flex-col bg-transparent border-l border-[var(--border-subtle)] overflow-hidden ${
              !selectedMessageId && !isComposeOpen ? 'hidden md:flex' : 'flex'
            }`}
          >
            {selectedMessageId && selectedMessage ? (
              <MessageThread
                message={selectedMessage}
                onBack={handleBackFromThread}
                onSend={handleSendMessage}
                onAttachData={handleOpenDataSharing}
              />
            ) : isComposeOpen ? (
              <ComposeMessage
                onCancel={handleCloseCompose}
                onSend={handleSendMessage}
                onAttachData={handleOpenDataSharing}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-[var(--brand-primary-subtle)] flex items-center justify-center mb-4">
                  <Send className="h-8 w-8 text-[var(--brand-primary)]" />
                </div>
                <h3 className="text-xl font-medium text-[var(--text-primary)] mb-2">
                  Your Messages
                </h3>
                <p className="text-[var(--text-secondary)] mb-6 max-w-md">
                  Select a conversation to view or start a new message to communicate
                  with your healthcare providers.
                </p>
                <button
                  onClick={handleOpenCompose}
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg hover:bg-[var(--brand-primary-hover)] transition-colors"
                >
                  <Plus size={16} />
                  <span>New Message</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* TAB: Secure Messages                                                */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'secure' && (
        <div className="grid md:grid-cols-[350px_1fr] flex-1 rounded-3xl border border-[var(--border-subtle)] shadow-sm bg-white/70 dark:bg-slate-800/60 backdrop-blur-md overflow-hidden mt-4">
          {/* Secure contacts sidebar */}
          <div
            className={`flex flex-col bg-transparent overflow-hidden ${
              selectedSecureProviderId ? 'hidden md:flex' : 'flex'
            }`}
          >
            {/* Sidebar header */}
            <div className="flex-shrink-0 p-4 border-b border-[var(--border-subtle)]">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-medium text-[var(--text-primary)]">
                  Encrypted Conversations
                </span>
              </div>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Messages are end-to-end encrypted with AES-GCM-256
              </p>
            </div>

            {/* Secure contacts list */}
            <div className="flex-1 overflow-y-auto">
              {SECURE_CONTACTS.map((provider) => {
                const isSelected = provider.id === selectedSecureProviderId;
                return (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => handleSelectSecureProvider(provider.id)}
                    className={`
                      w-full p-4 border-b border-[var(--border-subtle)] text-left
                      hover:bg-[var(--background-interactive)] transition-colors
                      ${isSelected
                        ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-l-blue-500 dark:border-l-blue-400 pl-[calc(1rem-4px)]'
                        : ''
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div
                        className={`w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''
                        }`}
                      >
                        <span className="text-emerald-700 dark:text-emerald-400 font-semibold">
                          {provider.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`font-medium text-sm truncate ${
                              isSelected
                                ? 'text-blue-600 dark:text-blue-300'
                                : 'text-[var(--text-primary)]'
                            }`}
                          >
                            {provider.name}
                          </span>
                          {/* Lock badge indicating E2E on this conversation */}
                          <Lock className="w-3 h-3 text-emerald-600 dark:text-emerald-400 flex-shrink-0" aria-label="End-to-end encrypted" />
                        </div>
                        <span className="text-xs text-[var(--text-secondary)]">
                          {provider.role}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* SecureChat panel */}
          <div
            className={`flex flex-col bg-transparent border-l border-[var(--border-subtle)] overflow-hidden ${
              !selectedSecureProviderId ? 'hidden md:flex' : 'flex'
            }`}
          >
            {selectedSecureProvider && user?.id ? (
              <SecureChat
                localUserId={user.id}
                provider={selectedSecureProvider}
                onBack={handleBackFromSecureChat}
                onPublicKeyReady={handlePublicKeyReady}
              />
            ) : (
              /* Empty state for the secure panel */
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-4">
                  <Lock className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-xl font-medium text-[var(--text-primary)] mb-2">
                  Secure Messaging
                </h3>
                <p className="text-[var(--text-secondary)] mb-3 max-w-sm">
                  Select a provider from the list to start an end-to-end encrypted
                  conversation.
                </p>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/30">
                  <Lock className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-xs text-emerald-700 dark:text-emerald-300">
                    Protected with ECDH P-256 + AES-GCM-256
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Health Data Sharing Modal */}
      {isDataSharingOpen && (
        <HealthDataSharing
          onClose={handleCloseDataSharing}
          onSelect={(data) => console.log('Selected health data:', data)}
        />
      )}
    </div>
  );
};

export default Messages;
